/**
 * Derive a human-readable page title from a route pathname so GA4's
 * Pages and Screens report shows content names instead of raw URLs.
 *
 * Lookup order: route params → content registries (lessons, worlds,
 * characters, selected story) → path-based fallback.
 */
import { getGuidedLesson } from './guidedLessonRegistry';
import { getWorldExplorer } from '../components/worldExplorer';
import { getCharacter } from './characters';

const BRAND = 'Explore';

// Matches ITEM_SESSION_KEY in StoryPlayerPage — set when a story is selected.
const STORY_ITEM_SESSION_KEY = 'explore_stories_selected_item';

/** First path segments that are real routes; anything else in slot 0 is a locale prefix. */
const KNOWN_ROOTS = new Set([
  '', 'about', 'topics', 'stories', 'chat', 'auth', 'settings', 'miniapps',
  'launch-checklist', 'restricted', 'search-result', 'search-result.html',
  's', 'g', 'e', 'f', 'i', 'n', 'v',
]);

const SEARCH_TYPE_ROOTS = new Set(['s', 'g', 'e', 'f', 'i', 'n', 'v']);

function titleCaseSlug(slug) {
  return String(slug)
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function characterDisplayName(param) {
  return getCharacter(param)?.name ?? titleCaseSlug(param);
}

function selectedStoryTitle(storyId) {
  try {
    const raw = sessionStorage.getItem(STORY_ITEM_SESSION_KEY);
    const item = raw ? JSON.parse(raw) : null;
    if (item?.title && String(item.id) === String(storyId)) return item.title;
  } catch { /* sessionStorage unavailable */ }
  return null;
}

/**
 * @param {string} pathname e.g. "/topics/robotics/lesson/gears-and-rpm"
 * @returns {string} e.g. "Explore — Gears and RPM (Robotics)"
 */
export function derivePageTitle(pathname) {
  const segments = String(pathname || '/')
    .split('/')
    .filter(Boolean);

  // Strip locale prefix (routes like /:locale/topics/...)
  if (segments.length > 0 && !KNOWN_ROOTS.has(segments[0]) && KNOWN_ROOTS.has(segments[1] ?? '')) {
    segments.shift();
  }

  const [root, second, third, fourth] = segments;

  if (!root) return `${BRAND} — Home`;

  if (root === 'about') return `${BRAND} — About`;

  if (root === 'search-result' || root === 'search-result.html') {
    return `${BRAND} — Search Results`;
  }

  if (SEARCH_TYPE_ROOTS.has(root)) return `${BRAND} — Search`;

  if (root === 'topics') {
    // /topics/:worldId/lesson/:lessonId
    if (third === 'lesson' && fourth) {
      const worldId = second;
      const lesson = getGuidedLesson(worldId, fourth);
      const worldLabel = getWorldExplorer(worldId)?.title ?? titleCaseSlug(worldId);
      return lesson?.title
        ? `${BRAND} — ${lesson.title} (${worldLabel})`
        : `${BRAND} — ${fourth}`;
    }
    // /topics and /topics/explorer both list worlds/lessons
    if (!second || second === 'explorer') return `${BRAND} — All Worlds`;
    // /topics/:worldId
    const world = getWorldExplorer(second);
    return `${BRAND} — ${world?.title ?? titleCaseSlug(second)} World`;
  }

  if (root === 'stories') {
    if (!second) return `${BRAND} — Stories`;
    const character = characterDisplayName(second);
    // /stories/:character/:storyId
    if (third) {
      const storyTitle = selectedStoryTitle(third);
      return storyTitle
        ? `${BRAND} — ${storyTitle} (${character})`
        : `${BRAND} — ${character} Story`;
    }
    // /stories/:character
    return `${BRAND} — ${character} Stories`;
  }

  if (root === 'chat') return `${BRAND} — Chat`;
  if (root === 'settings') return `${BRAND} — Settings`;
  if (root === 'miniapps') return `${BRAND} — Mini Apps`;

  return BRAND;
}
